import { Component, ElementRef, Input, OnInit } from '@angular/core';

import { ActionApiService } from '../../../Services/action-api/action-api.service';
import { ObservablesService } from '../../../Services/observables/observables';
import { ReportEventService } from '../../../Services/report-event/report-event.service';
import { SplunkAnalyticsService } from '../../../Services/splunk-analytics/splunk-analytics-service';
import { StoreService } from '../../../Services/store';
import { LayoutService } from 'src/app/Services/layout/layout.service';

@Component({
	selector: 'progress-meter',
	template: `
		<!-- For desktop/tablet resolution > 768px -->
		<div class="progress">
			<ul class="sections">
				<li
					class="section"
					[ngClass]="{
						completed: section.completed,
						active: section.active && !hasActiveState(section),
						inactive: !section.active,
						focused: section.focused
					}"
					*ngFor="let section of progressMeter.sections; let i = index"
					[attr.aria-disabled]="isDisabled(section)"
					[attr.aria-hidden]="isDisabled(section)"
				>
					<div class="section-wrapper" *ngIf="section.visible">
						<div class="icon-container">
							<i class="icon" [class]="sectionsIconArray[i]"></i>
						</div>

						<a
							[tabindex]="isDisabled(section) ? -1 : 0"
							(focus)="section.focused = true"
							(focusout)="section.focused = false"
							(keypress)="
								$event.key == 'Enter'
									? onClick(progressMeter.name, section.ajaxInfo.action, section.ajaxInfo.stage, section.completed)
									: false
							"
						>
							<span
								class="title-section"
								tabindex="-1"
								[attr.name]="section.title"
								(click)="
									onClick(progressMeter.name, section.ajaxInfo.action, section.ajaxInfo.stage, section.completed)
								"
								gtm-handler
								events="click"
								handlerType="button"
								[label]="section.ajaxInfo.stage + '_Navigation'"
								action="LinkClick"
								[attributeVal]="section.ajaxInfo.stage + '_Navigation'"
								(mouseover)="hideAnchorTitleOnMouseOver($event)"
								(mouseout)="resetAnchorTitle($event, section)"
							>
								{{ section.title }}
							</span>
						</a>
					</div>

					<ul class="states" *ngIf="hasSubStates(section)">
						<li
							class="state"
							[attr.aria-disabled]="isDisabled(state)"
							[attr.aria-hidden]="isDisabled(state)"
							*ngFor="let state of section.states"
							[ngClass]="{
								completed: state.completed,
								active: state.active,
								inactive: !state.active
							}"
						>
							<a
								[tabindex]="isDisabled(state) ? -1 : 0"
								class="title-state"
								[attr.name]="state.title"
								(click)="onClick(progressMeter.name, state.ajaxInfo.action, state.ajaxInfo.stage, state.completed)"
								gtm-handler
								events="click"
								handlerType="button"
								[label]="state.ajaxInfo.stage + '_Navigation'"
								action="LinkClick"
								[attributeVal]="state.ajaxInfo.stage + '_Navigation'"
								(keypress)="
									onKeyPress($event, progressMeter.name, state.ajaxInfo.action, state.ajaxInfo.stage, section.completed)
								"
								(mouseover)="hideAnchorTitleOnMouseOver($event)"
								(mouseout)="resetAnchorTitle($event, section)"
							>
								{{ state.title }}
							</a>
						</li>
					</ul>
				</li>
			</ul>
		</div>
		<!-- For mobile resolution < 768px -->
		<div class="progress-mobile">
			<div class="top-sections">
				<ng-container *ngFor="let section of progressMeter.sections">
					<div
						class="top-section"
						[ngClass]="{
							completed: section.completed,
							active: section.active,
							inactive: !section.active
						}"
						*ngIf="section.visible"
					>
						<div class="timeline">
							<span
								[ngStyle]="this.calcWidth(section)"
								*ngIf="section.completed || section.active"
								[ngClass]="{
									completed: section.completed,
									'timeline-active': section.active
								}"
							></span>
						</div>
					</div>
				</ng-container>
			</div>
		</div>
	`,
	styleUrls: ['./progressmeter.component.scss'],
})
export class ProgressMeterComponent implements OnInit {
	@Input() progressMeter: progressMeter;
	ajaxInfo: any;
	mobileSectionsWidth: any = [];
	public isOpen: boolean = false;
	sectionsIconArray: string[] = [];
	sectionsMobileWidths: string[];

	constructor(
		private storeService: StoreService,
		private el: ElementRef,
		private splunkAnalyticsService: SplunkAnalyticsService,
		private actionApiService: ActionApiService,
		private reportEvent: ReportEventService,
		private observablesService: ObservablesService //private layoutService: LayoutService
	) {}

	async ngOnInit() {
		this.mapSectionWithFocus();
		this.mapSectionIcons();
	}

	mapSectionWithFocus(): void {
		this.progressMeter.sections.map((section) => (section['focused'] = false));
	}

	mapSectionIcons(): void {
		for (let section of this.progressMeter.sections) {
			this.sectionsIconArray.push(this.getIconClass(section));
		}
	}

	getIconClass(section) {
		if (section.completed) {
			return 'icon-progressmeter-checkmark';
		}

		return `icon-progressmeter-${section.icon}` || 'icon-progressmeter-checkmark';
	}

	hasSubStates(section) {
		return section.states;
	}

	hasActiveState(section): boolean {
		return section.states && section.states.length && section.states.some((state) => state.active);
	}

	onClick(elementSrc, action, stage, completed) {
		this.reportEvent.addPageLoad('none');
		this.ajaxInfo = {
			action: action,
			stage: stage,
		};
		return completed && this.actionApiService.ajaxAction(this.ajaxInfo, 'page', elementSrc);
	}

	setTitle(elem) {
		if (elem) {
			if (!elem.active && !elem.completed) {
				return 'disabled';
			}
		}
	}

	isDisabled(elem) {
		return this.setTitle(elem) == 'disabled';
	}

	onKeyPress(event, elementSrc, action, stage, completed) {
		// if 'ENTER' key pressed
		if (event.keyCode === 13) {
			this.onClick(elementSrc, action, stage, completed);
		}
	}

	// hide (on mouse over) anchor's title so the tooltip will not be visible but still be accessed
	// by screen reader when tabbing through the progressmeter
	hideAnchorTitleOnMouseOver(event) {
		event.srcElement.removeAttribute('title');
	}

	// reset the title after mouse out event fires (the title attribute for the screen reader to be able to read again)
	resetAnchorTitle(event, section) {
		const titleValue = this.setTitle(section);
		event.srcElement.title = titleValue;
	}

	calcWidth(section) {
		if (this.mobileSectionsWidth[section.icon]) {
			return this.mobileSectionsWidth[section.icon];
		}

		let width;
		if (!section.visible) {
			return;
		}

		if (section.states != null && section.states.length > 0) {
			// join section & sub-section
			let list = [{ ...section }];
			delete list[0].states;
			list = list.concat(section.states);
			// filter by completed & visible
			// const visibleOnly = list.filter(t => t.visible);
			const notCompleted = list.filter((t) => !t.completed).length;
			const completed = list.filter((t) => t.completed).length;
			// set width
			if (list.length === completed) {
				width = 100;
			} else if (notCompleted === 0) {
				width = 0;
			} else {
				width = (completed / list.length) * 100;
			}
		} else if (!section.completed && section.name !== 'YourRates') {
			// don't have sub-section and not completed and not last section (in mobile it should look full and in pc not)
			width = 0;
		} else {
			// don't have sub-section and completed
			width = 100;
		}
		width = width + '%';
		this.mobileSectionsWidth[section.icon] = { width: width };
		return this.mobileSectionsWidth[section.icon];
	}
}
